export const SURVEY_AUDIENCE_TYPES = Object.freeze({
  FARMER_SINGLE_RESPONSE: "Farmer (Single response for all farms)",
  FARMER_SEPARATE_RESPONSE: "Farmer (Separate response for each farm)",
  CONNECTED_FARMER: "Connected_Farmer",
  CONNECTED_CACAO: "Connected_Cacao",
  SUB_ORGANIZATION: "Sub-Organization",
  PARTNER: "Supplier",
});

export const RESPONDENT_SELECTION_TYPES = Object.freeze({
  ALL_USERS: "All Users",
  SELECT_USERS: "Select Users",
  ALL_SUB_ORGANIZATIONS: "All Sub-Organizations",
  SELECT_SUB_ORGANIZATIONS: "Select Sub-Organizations",
});

export const SURVEY_MODULES = Object.freeze({
  CONNECTED_FARMER: { label: "Connected Farmer", value: "Connected Farmer" },
  CONNECTED_COFFEE: { label: "Connected Coffee", value: "Connected Coffee" },
  CONNECTED_CACAO: { label: "Connected Cacao", value: "Connected Cacao" },
  ESG_PLATFORM: { label: "ESG Platform", value: "ESG" },
  CARBON_CREDIT: { label: "Carbon Credit", value: "Carbon Credit" },
  SURVEY: { label: "Survey", value: "Survey" },
});

export const SURVEY_MODULE_OPTIONS = Object.freeze([
  SURVEY_MODULES.ESG_PLATFORM,
  SURVEY_MODULES.SURVEY,
]);

export const SURVEY_POST_ANSWER_ACTIONS = Object.freeze({
  RECOMMENDATIONS: {
    label: "Recommendations",
    value: "Recommendations",
    flagKey: "recommendations",
    payloadEnabledKey: "isRecommendationEnabled",
  },
  GET_DATA: {
    label: "Get Data",
    value: "Get Data",
    flagKey: "getData",
    payloadEnabledKey: "isGetDataEnabled",
  },
  ACTION_PLAN: {
    label: "Action Plan",
    value: "Action Plan",
    flagKey: "actionPlan",
    payloadEnabledKey: "isActionPlanEnabled",
  },
  DIMITRA_MODULES: {
    label: "Dimitra Modules",
    value: "Dimitra Modules",
    flagKey: "dimitraModules",
    payloadEnabledKey: "isDimitraModulesEnabled",
  },
});

export const SURVEY_QUESTION_TYPES = Object.freeze({
  ALL: { label: "All", value: "all" },
  TEXT_FIELD: { label: "Text Field", value: "text-field" },
  TEXT_AREA: { label: "Text Area", value: "text-area" },
  NUMERIC: { label: "Text Field Numeric", value: "numeric" },
  RADIO_BUTTON: { label: "Radio Button", value: "radio-button" },
  CHECK_BOXES: { label: "Check Boxes", value: "check-boxes" },
  GET_DATA: { label: "Get Data", value: "get-data" },
  DIGITAL_SIGNATURE: { label: "Digital Signature", value: "digital-signature" },
  ATTACH_FILES: { label: "Attach Files", value: "attach-files" },
  DIMITRA_MODULE: { label: "Dimitra Module", value: "dimitra-module" },
});

const surveyUserSelectionOptions = Object.freeze([
  {
    label: "All Users",
    value: RESPONDENT_SELECTION_TYPES.ALL_USERS,
    selectionTarget: "surveyUser",
    selectionKey: "selectedSurveyUserIds",
    requiresSelection: false,
    buttonLabel: "Select Users",
    selectedLabel: "Users",
    emptyMessage: "Please select at least one Survey User",
  },
  {
    label: "Select Specific Users",
    value: RESPONDENT_SELECTION_TYPES.SELECT_USERS,
    selectionTarget: "surveyUser",
    selectionKey: "selectedSurveyUserIds",
    requiresSelection: true,
    buttonLabel: "Select Users",
    selectedLabel: "Users",
    emptyMessage: "Please select at least one Survey User",
  },
]);

const subOrganizationSelectionOptions = Object.freeze([
  {
    label: "All Sub-Organizations",
    value: RESPONDENT_SELECTION_TYPES.ALL_SUB_ORGANIZATIONS,
    selectionTarget: "subOrganization",
    selectionKey: "selectedSubOrganizationIds",
    requiresSelection: false,
    buttonLabel: "Select Sub-Organizations",
    selectedLabel: "Sub-Organizations",
    emptyMessage: "Please select at least one Sub-Organization",
  },
  {
    label: "Select Sub-Organizations",
    value: RESPONDENT_SELECTION_TYPES.SELECT_SUB_ORGANIZATIONS,
    selectionTarget: "subOrganization",
    selectionKey: "selectedSubOrganizationIds",
    requiresSelection: true,
    buttonLabel: "Select Sub-Organizations",
    selectedLabel: "Sub-Organizations",
    emptyMessage: "Please select at least one Sub-Organization",
  },
]);

const surveyUserRespondentSelection = Object.freeze({
  defaultValue: RESPONDENT_SELECTION_TYPES.ALL_USERS,
  allowedValues: [
    RESPONDENT_SELECTION_TYPES.ALL_USERS,
    RESPONDENT_SELECTION_TYPES.SELECT_USERS,
  ],
  selectionTarget: "surveyUser",
  options: surveyUserSelectionOptions,
});

const subOrganizationRespondentSelection = Object.freeze({
  defaultValue: RESPONDENT_SELECTION_TYPES.ALL_SUB_ORGANIZATIONS,
  allowedValues: [
    RESPONDENT_SELECTION_TYPES.ALL_SUB_ORGANIZATIONS,
    RESPONDENT_SELECTION_TYPES.SELECT_SUB_ORGANIZATIONS,
  ],
  selectionTarget: "subOrganization",
  options: subOrganizationSelectionOptions,
});

const VALIDATION_MESSAGES = Object.freeze({
  unsupportedAudienceType: "Selected survey audience type is not supported",
  attachedModuleRequired: "Attach Survey to Module is required",
  attachedModuleUnavailable: "No module options are available for the selected survey audience type",
  attachedModuleInvalid: "Selected module is not available for the selected survey audience type",
  respondentSelectionInvalid: "Selected respondent selection is not available for the selected survey audience type",
  questionRespondentTypeInvalid: "Selected question answerer is not available for the selected survey module and audience",
});

const buildAudienceConfig = ({
  label,
  value,
  respondentSelection,
  allowedModules,
  allowedPostAnswerActions,
  allowedQuestionTypes,
  noModuleOptionsMessage,
}) => Object.freeze({
  label,
  value,
  respondentSelection,
  visibility: Object.freeze({
    attachSurveyToModule: true,
  }),
  validation: Object.freeze({
    attachedModuleRequired: true,
  }),
  allowedModules: Object.freeze(allowedModules),
  allowedPostAnswerActions: Object.freeze(allowedPostAnswerActions || Object.values(SURVEY_POST_ANSWER_ACTIONS).map(({ value: actionValue }) => actionValue)),
  allowedQuestionTypes: Object.freeze(allowedQuestionTypes || Object.values(SURVEY_QUESTION_TYPES).map(({ value: questionTypeValue }) => questionTypeValue)),
  messages: Object.freeze({
    noModuleOptions: noModuleOptionsMessage || VALIDATION_MESSAGES.attachedModuleUnavailable,
  }),
});

export const FARMER_SURVEY_RESPONDENT_TYPES = Object.freeze([
  SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
  SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
  SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
]);

export const FARMER_QUESTION_RESPONDENT_TYPES = Object.freeze([
  SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
]);

export const SUB_ORGANIZATION_QUESTION_RESPONDENT_TYPES = Object.freeze([
  SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
]);

export const QUESTION_RESPONDENT_TYPE_OPTIONS = Object.freeze([
  {
    label: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
    value: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  },
  {
    label: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
    value: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
  },
  {
    label: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
    value: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
  },
]);

export const QUESTION_RESPONDENT_TYPE_VALUES = Object.freeze(
  QUESTION_RESPONDENT_TYPE_OPTIONS.map(({ value }) => value)
);

export const QUESTION_RESPONDENT_RULES = Object.freeze([
  Object.freeze({
    key: "sub-organization",
    moduleValues: Object.freeze([SURVEY_MODULES.ESG_PLATFORM.value]),
    surveyRespondentTypes: Object.freeze([SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION]),
    allowedQuestionRespondentTypes: SUB_ORGANIZATION_QUESTION_RESPONDENT_TYPES,
    defaultQuestionRespondentType: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
  }),
  Object.freeze({
    key: "survey-sub-organization",
    moduleValues: Object.freeze([SURVEY_MODULES.SURVEY.value]),
    surveyRespondentTypes: Object.freeze([SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION]),
    allowedQuestionRespondentTypes: SUB_ORGANIZATION_QUESTION_RESPONDENT_TYPES,
    defaultQuestionRespondentType: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
  }),
  Object.freeze({
    key: "farmer-modules",
    moduleValues: Object.freeze([
      SURVEY_MODULES.CONNECTED_FARMER.value,
      SURVEY_MODULES.CONNECTED_CACAO.value,
    ]),
    surveyRespondentTypes: FARMER_SURVEY_RESPONDENT_TYPES,
    allowedQuestionRespondentTypes: FARMER_QUESTION_RESPONDENT_TYPES,
    defaultQuestionRespondentType: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  }),
  Object.freeze({
    key: "esg-farmer-audiences",
    moduleValues: Object.freeze([SURVEY_MODULES.ESG_PLATFORM.value]),
    surveyRespondentTypes: FARMER_SURVEY_RESPONDENT_TYPES,
    allowedQuestionRespondentTypes: FARMER_QUESTION_RESPONDENT_TYPES,
    defaultQuestionRespondentType: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  }),
  Object.freeze({
    key: "survey-farmer-audiences",
    moduleValues: Object.freeze([SURVEY_MODULES.SURVEY.value]),
    surveyRespondentTypes: FARMER_SURVEY_RESPONDENT_TYPES,
    allowedQuestionRespondentTypes: FARMER_QUESTION_RESPONDENT_TYPES,
    defaultQuestionRespondentType: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
  }),
]);

export const DEFAULT_SURVEY_BUILDER_CONFIG = Object.freeze({
  audienceTypes: Object.freeze([
    { label: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE, value: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE },
    { label: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE, value: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE },
    { label: "Connected Farmer", value: SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER },
    { label: "Connected Cacao", value: SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO },
    { label: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION, value: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION },
    { label: "Partner", value: SURVEY_AUDIENCE_TYPES.PARTNER },
  ]),
  moduleOptions: SURVEY_MODULE_OPTIONS,
  audienceTypeConfig: Object.freeze({
    [SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE]: buildAudienceConfig({
      label: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
      value: SURVEY_AUDIENCE_TYPES.FARMER_SINGLE_RESPONSE,
      respondentSelection: surveyUserRespondentSelection,
      allowedModules: [
        SURVEY_MODULES.CONNECTED_FARMER.value,
        SURVEY_MODULES.CONNECTED_CACAO.value,
      ],
    }),
    [SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE]: buildAudienceConfig({
      label: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
      value: SURVEY_AUDIENCE_TYPES.FARMER_SEPARATE_RESPONSE,
      respondentSelection: surveyUserRespondentSelection,
      allowedModules: [
        SURVEY_MODULES.CONNECTED_FARMER.value,
        SURVEY_MODULES.CONNECTED_CACAO.value,
      ],
    }),
    [SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER]: buildAudienceConfig({
      label: "Connected Farmer",
      value: SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
      respondentSelection: surveyUserRespondentSelection,
      allowedModules: [
        SURVEY_MODULES.ESG_PLATFORM.value,
        SURVEY_MODULES.CONNECTED_FARMER.value,
      ],
    }),
    [SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO]: buildAudienceConfig({
      label: "Connected Cacao",
      value: SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
      respondentSelection: surveyUserRespondentSelection,
      allowedModules: [
        SURVEY_MODULES.ESG_PLATFORM.value,
        SURVEY_MODULES.CONNECTED_CACAO.value,
      ],
    }),
    [SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION]: buildAudienceConfig({
      label: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
      value: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
      respondentSelection: subOrganizationRespondentSelection,
      allowedModules: [
        SURVEY_MODULES.ESG_PLATFORM.value,
      ],
      allowedPostAnswerActions: [
        SURVEY_POST_ANSWER_ACTIONS.RECOMMENDATIONS.value,
        SURVEY_POST_ANSWER_ACTIONS.GET_DATA.value,
        SURVEY_POST_ANSWER_ACTIONS.ACTION_PLAN.value,
      ],
      allowedQuestionTypes: [
        SURVEY_QUESTION_TYPES.ALL.value,
        SURVEY_QUESTION_TYPES.TEXT_FIELD.value,
        SURVEY_QUESTION_TYPES.TEXT_AREA.value,
        SURVEY_QUESTION_TYPES.NUMERIC.value,
        SURVEY_QUESTION_TYPES.RADIO_BUTTON.value,
        SURVEY_QUESTION_TYPES.CHECK_BOXES.value,
        SURVEY_QUESTION_TYPES.GET_DATA.value,
        SURVEY_QUESTION_TYPES.DIGITAL_SIGNATURE.value,
        SURVEY_QUESTION_TYPES.ATTACH_FILES.value,
      ],
    }),
    [SURVEY_AUDIENCE_TYPES.PARTNER]: buildAudienceConfig({
      label: "Partner",
      value: SURVEY_AUDIENCE_TYPES.PARTNER,
      respondentSelection: surveyUserRespondentSelection,
      allowedModules: [],
    }),
  }),
  moduleResponseConfig: Object.freeze({
    [SURVEY_MODULES.ESG_PLATFORM.value]: Object.freeze({
      defaultRespondentType: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
      showRespondentType: true,
      showRespondentSelection: true,
      allowedRespondentTypes: Object.freeze([
        SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
        SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
        SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
      ]),
    }),
    [SURVEY_MODULES.SURVEY.value]: Object.freeze({
      defaultRespondentType: SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
      showRespondentType: true,
      showRespondentSelection: false,
      allowedRespondentTypes: Object.freeze([
        SURVEY_AUDIENCE_TYPES.SUB_ORGANIZATION,
        SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
        SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
      ]),
    }),
    [SURVEY_MODULES.CONNECTED_FARMER.value]: Object.freeze({
      defaultRespondentType: SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
      showRespondentType: false,
      showRespondentSelection: true,
      allowedRespondentTypes: Object.freeze([
        SURVEY_AUDIENCE_TYPES.CONNECTED_FARMER,
      ]),
    }),
    [SURVEY_MODULES.CONNECTED_CACAO.value]: Object.freeze({
      defaultRespondentType: SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
      showRespondentType: false,
      showRespondentSelection: true,
      allowedRespondentTypes: Object.freeze([
        SURVEY_AUDIENCE_TYPES.CONNECTED_CACAO,
      ]),
    }),
  }),
  questionRespondentTypeOptions: QUESTION_RESPONDENT_TYPE_OPTIONS,
  questionRespondentRules: QUESTION_RESPONDENT_RULES,
  fallbackAudienceTypeConfig: Object.freeze({
    label: "Unsupported Audience",
    value: null,
    unsupported: true,
    respondentSelection: Object.freeze({
      defaultValue: null,
      allowedValues: [],
      selectionTarget: null,
      options: [],
    }),
    visibility: Object.freeze({
      attachSurveyToModule: true,
    }),
    validation: Object.freeze({
      attachedModuleRequired: true,
    }),
    allowedModules: Object.freeze([]),
    allowedPostAnswerActions: Object.freeze([]),
    allowedQuestionTypes: Object.freeze([SURVEY_QUESTION_TYPES.ALL.value]),
    messages: Object.freeze({
      noModuleOptions: VALIDATION_MESSAGES.unsupportedAudienceType,
    }),
  }),
  postAnswerActionOptions: Object.freeze(Object.values(SURVEY_POST_ANSWER_ACTIONS)),
  questionTypeOptions: Object.freeze(Object.values(SURVEY_QUESTION_TYPES)),
  validationMessages: VALIDATION_MESSAGES,
});

const cloneConfig = (value) => JSON.parse(JSON.stringify(value));

export const normalizeSurveyBuilderConfig = (config = {}) => {
  const fallback = cloneConfig(DEFAULT_SURVEY_BUILDER_CONFIG);
  const audienceTypeConfig = { ...fallback.audienceTypeConfig };
  Object.entries(config.audienceTypeConfig || {}).forEach(([key, value]) => {
    audienceTypeConfig[key] = {
      ...(fallback.audienceTypeConfig[key] || {}),
      ...value,
    };
  });

  Object.keys(audienceTypeConfig).forEach((key) => {
    const audienceConfig = audienceTypeConfig[key];
    if (audienceConfig?.respondentSelection?.selectionTarget === "surveyUser") {
      const respondentSelection = { ...audienceConfig.respondentSelection };
      respondentSelection.defaultValue = RESPONDENT_SELECTION_TYPES.ALL_USERS;
      audienceTypeConfig[key] = {
        ...audienceConfig,
        respondentSelection: Object.freeze(respondentSelection),
      };
    }
  });

  return {
    ...fallback,
    ...config,
    audienceTypes: Array.isArray(config.audienceTypes) && config.audienceTypes.length
      ? config.audienceTypes
      : fallback.audienceTypes,
    moduleOptions: Array.isArray(config.moduleOptions)
      ? config.moduleOptions
      : fallback.moduleOptions,
    audienceTypeConfig,
    moduleResponseConfig: {
      ...fallback.moduleResponseConfig,
      ...(config.moduleResponseConfig || {}),
    },
    questionRespondentTypeOptions: Array.isArray(config.questionRespondentTypeOptions)
      ? config.questionRespondentTypeOptions
      : fallback.questionRespondentTypeOptions,
    questionRespondentRules: Array.isArray(config.questionRespondentRules)
      ? config.questionRespondentRules
      : fallback.questionRespondentRules,
    fallbackAudienceTypeConfig: {
      ...fallback.fallbackAudienceTypeConfig,
      ...(config.fallbackAudienceTypeConfig || {}),
    },
    postAnswerActionOptions: Array.isArray(config.postAnswerActionOptions)
      ? config.postAnswerActionOptions
      : fallback.postAnswerActionOptions,
    questionTypeOptions: Array.isArray(config.questionTypeOptions)
      ? config.questionTypeOptions
      : fallback.questionTypeOptions,
    validationMessages: {
      ...fallback.validationMessages,
      ...(config.validationMessages || {}),
    },
  };
};

export const cloneSurveyBuilderConfig = () => normalizeSurveyBuilderConfig();

export const getAudienceTypeConfig = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  config.audienceTypeConfig?.[respondentType] || config.fallbackAudienceTypeConfig
);

export const getSurveyModuleResponseConfig = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  config.moduleResponseConfig?.[attachedModule] || null
);

export const getAllowedRespondentTypesForModule = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const moduleConfig = getSurveyModuleResponseConfig(attachedModule, config);
  return moduleConfig?.allowedRespondentTypes || (config.audienceTypes || []).map(({ value }) => value);
};

export const getRespondentTypeOptionsForModule = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const allowedRespondentTypes = getAllowedRespondentTypesForModule(attachedModule, config);
  const optionByValue = (config.audienceTypes || []).reduce((acc, option) => {
    acc[option.value] = option;
    return acc;
  }, {});
  return allowedRespondentTypes.map((value) => optionByValue[value]).filter(Boolean);
};

export const getDefaultRespondentTypeForModule = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const moduleConfig = getSurveyModuleResponseConfig(attachedModule, config);
  return moduleConfig?.defaultRespondentType || null;
};

export const shouldShowRespondentTypeForModule = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const moduleConfig = getSurveyModuleResponseConfig(attachedModule, config);
  return moduleConfig ? moduleConfig.showRespondentType !== false : true;
};

export const shouldShowRespondentSelectionForModule = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const moduleConfig = getSurveyModuleResponseConfig(attachedModule, config);
  return moduleConfig ? moduleConfig.showRespondentSelection !== false : true;
};

export const isRespondentTypeAllowedForModule = (
  attachedModule,
  respondentType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => !attachedModule || getAllowedRespondentTypesForModule(attachedModule, config).includes(respondentType);

export const getAssignableModuleValues = (config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  (config.moduleOptions || []).map(({ value }) => value)
);

export const isSurveyModuleAssignable = (attachedModule, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAssignableModuleValues(config).includes(attachedModule)
);

const matchesQuestionRespondentRule = (rule, { attachedModule, respondentType } = {}) => {
  const moduleMatches = !rule.moduleValues?.length || rule.moduleValues.includes(attachedModule);
  const respondentTypeMatches = !rule.surveyRespondentTypes?.length
    || rule.surveyRespondentTypes.includes(respondentType);

  return moduleMatches && respondentTypeMatches;
};

export const getQuestionRespondentRuleForSurvey = (
  { attachedModule, respondentType } = {},
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => (
  (config.questionRespondentRules || []).find((rule) => (
    matchesQuestionRespondentRule(rule, { attachedModule, respondentType })
  )) || null
);

export const getAllowedQuestionRespondentTypesForSurvey = (
  { attachedModule, respondentType } = {},
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  const rule = getQuestionRespondentRuleForSurvey({ attachedModule, respondentType }, config);
  return rule?.allowedQuestionRespondentTypes || [];
};

export const getDefaultQuestionRespondentTypeForSurvey = (
  { attachedModule, respondentType } = {},
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  const rule = getQuestionRespondentRuleForSurvey({ attachedModule, respondentType }, config);
  return rule?.defaultQuestionRespondentType || null;
};

export const isQuestionRespondentTypeAllowedForSurvey = (
  { attachedModule, respondentType } = {},
  questionRespondentType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => (
  getAllowedQuestionRespondentTypesForSurvey({ attachedModule, respondentType }, config)
    .includes(questionRespondentType)
);

export const normalizeQuestionRespondentTypeForSurvey = (
  { attachedModule, respondentType } = {},
  questionRespondentType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => (
  isQuestionRespondentTypeAllowedForSurvey({ attachedModule, respondentType }, questionRespondentType, config)
    ? questionRespondentType
    : getDefaultQuestionRespondentTypeForSurvey({ attachedModule, respondentType }, config)
);

export const getQuestionRespondentTypeOptionsForSurvey = (
  { attachedModule, respondentType } = {},
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  const allowedValues = getAllowedQuestionRespondentTypesForSurvey({ attachedModule, respondentType }, config);
  const optionByValue = (config.questionRespondentTypeOptions || []).reduce((acc, option) => {
    acc[option.value] = option;
    return acc;
  }, {});

  return allowedValues.map((value) => optionByValue[value] || { label: value, value });
};

export const getRespondentSelectionOptions = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAudienceTypeConfig(respondentType, config).respondentSelection?.options || []
);

export const getRespondentSelectionOption = (
  respondentType,
  respondentSelectionType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => getRespondentSelectionOptions(respondentType, config)
  .find(({ value }) => value === respondentSelectionType);

export const getDefaultRespondentSelectionType = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAudienceTypeConfig(respondentType, config).respondentSelection?.defaultValue || null
);

export const getAllowedModuleValuesForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAudienceTypeConfig(respondentType, config).allowedModules || []
);

export const getAllowedModuleOptionsForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const allowedModuleValues = getAllowedModuleValuesForAudience(respondentType, config);
  return (config.moduleOptions || []).filter(({ value }) => allowedModuleValues.includes(value));
};

export const getAllowedPostAnswerActionValuesForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAudienceTypeConfig(respondentType, config).allowedPostAnswerActions || []
);

export const getAllowedQuestionTypeValuesForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  getAudienceTypeConfig(respondentType, config).allowedQuestionTypes || []
);

export const getPostAnswerActionOptionsForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const allowedActions = getAllowedPostAnswerActionValuesForAudience(respondentType, config);
  return (config.postAnswerActionOptions || []).filter(({ value }) => allowedActions.includes(value));
};

export const getQuestionTypeOptionsForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const allowedQuestionTypes = getAllowedQuestionTypeValuesForAudience(respondentType, config);
  return (config.questionTypeOptions || []).filter(({ value }) => allowedQuestionTypes.includes(value));
};

export const normalizePostAnswerActionsForAudience = (
  values,
  respondentType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const allowedActions = getAllowedPostAnswerActionValuesForAudience(respondentType, config);
  return values.filter((value) => allowedActions.includes(value));
};

export const normalizeQuestionTypesForAudience = (
  values,
  respondentType,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  const allowedQuestionTypes = getAllowedQuestionTypeValuesForAudience(respondentType, config);

  if (!Array.isArray(values) || values.length === 0 || values.includes(SURVEY_QUESTION_TYPES.ALL.value)) {
    return [SURVEY_QUESTION_TYPES.ALL.value];
  }

  const filteredValues = values.filter((value) => allowedQuestionTypes.includes(value));
  return filteredValues.length ? filteredValues : [SURVEY_QUESTION_TYPES.ALL.value];
};

export const shouldShowAttachedModuleField = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => (
  Boolean(getAudienceTypeConfig(respondentType, config).visibility?.attachSurveyToModule)
);

export const isAttachedModuleRequiredForAudience = (respondentType, config = DEFAULT_SURVEY_BUILDER_CONFIG) => {
  const audienceConfig = getAudienceTypeConfig(respondentType, config);
  return Boolean(
    shouldShowAttachedModuleField(respondentType, config)
    && audienceConfig.validation?.attachedModuleRequired
  );
};

export const isAttachedModuleAllowedForAudience = (
  respondentType,
  attachedModule,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => (
  isSurveyModuleAssignable(attachedModule, config)
  && (
    isRespondentTypeAllowedForModule(attachedModule, respondentType, config)
    || getAllowedModuleValuesForAudience(respondentType, config).includes(attachedModule)
  )
);

export const validateAttachedModuleValue = (
  respondentType,
  attachedModule,
  config = DEFAULT_SURVEY_BUILDER_CONFIG
) => {
  const audienceConfig = getAudienceTypeConfig(respondentType, config);
  const validationMessages = config.validationMessages || VALIDATION_MESSAGES;

  if (audienceConfig.unsupported) {
    return validationMessages.unsupportedAudienceType;
  }

  if (!shouldShowAttachedModuleField(respondentType, config) || !isAttachedModuleRequiredForAudience(respondentType, config)) {
    return true;
  }

  const allowedModuleValues = getAllowedModuleValuesForAudience(respondentType, config)
    .filter((value) => isSurveyModuleAssignable(value, config));
  const moduleRespondentTypeAllowed = attachedModule
    && isSurveyModuleAssignable(attachedModule, config)
    && isRespondentTypeAllowedForModule(attachedModule, respondentType, config);

  if (!allowedModuleValues.length && !moduleRespondentTypeAllowed) {
    return audienceConfig.messages?.noModuleOptions || validationMessages.attachedModuleUnavailable;
  }

  if (!attachedModule) {
    return validationMessages.attachedModuleRequired;
  }

  if (
    !isSurveyModuleAssignable(attachedModule, config)
    || !isAttachedModuleAllowedForAudience(respondentType, attachedModule, config)
  ) {
    return validationMessages.attachedModuleInvalid;
  }

  return true;
};
